import type { VoiceLiveEvent } from '../machine/types';

export const VOICE_LIVE_INSPECTOR_PROTOCOL_VERSION = 1 as const;
export const VOICE_LIVE_INSPECTOR_CHANNEL_PREFIX = 'fieldone:voice-live-inspector:';
export const VOICE_LIVE_INSPECTOR_WINDOW_PREFIX = 'fieldone-voice-live-inspector-';
export const VOICE_LIVE_INSPECTOR_ROUTE = '/support/voice-live-inspector';
export const VOICE_LIVE_INSPECTOR_MAX_ENTRIES = 500;

export type InspectorEventType = VoiceLiveEvent['type'];

export type InspectorStateValue = string | { [region: string]: InspectorStateValue };

export interface InspectorContext {
  avatarEverActive: boolean;
  degradedToAudio: boolean;
  errorMessage: string;
  hasIceServers: boolean;
  hasTurnServer: boolean;
  iceServerCount: number;
  initialAvatarFailures: number;
  socketIsAudioOnly: boolean;
  isVideoEnabled: boolean;
  retryCount: number;
  sessionReady: boolean;
  shouldReconnect: boolean;
}

export interface InspectorEvent {
  type: InspectorEventType | string;
  details?: Record<string, boolean | number | string>;
}

export interface InspectorTransition {
  actions: string[];
  eventType: string;
  guard?: string;
  source?: string;
  targets: string[];
}

export interface InspectorEntry {
  actorId: string;
  actions: string[];
  context: InspectorContext;
  event: InspectorEvent;
  previousState?: InspectorStateValue;
  sequence: number;
  state: InspectorStateValue;
  status: string;
  timestamp: number;
  transitions: InspectorTransition[];
}

interface InspectorMessageBase {
  protocolVersion: typeof VOICE_LIVE_INSPECTOR_PROTOCOL_VERSION;
  sessionId: string;
}

export interface InspectorReceiverReadyMessage extends InspectorMessageBase {
  kind: 'receiver-ready';
}

export interface InspectorReplayMessage extends InspectorMessageBase {
  entries: InspectorEntry[];
  kind: 'replay';
}

export interface InspectorEntryMessage extends InspectorMessageBase {
  entry: InspectorEntry;
  kind: 'entry';
}

export interface InspectorHeartbeatMessage extends InspectorMessageBase {
  kind: 'heartbeat';
  timestamp: number;
}

export interface InspectorClearMessage extends InspectorMessageBase {
  kind: 'clear';
}

export interface InspectorSourceClosedMessage extends InspectorMessageBase {
  kind: 'source-closed';
}

export type InspectorReceiverMessage = InspectorReceiverReadyMessage | InspectorClearMessage;

export type InspectorSourceMessage =
  | InspectorReplayMessage
  | InspectorEntryMessage
  | InspectorHeartbeatMessage
  | InspectorSourceClosedMessage;

export const getVoiceLiveInspectorChannelName = (sessionId: string) =>
  `${VOICE_LIVE_INSPECTOR_CHANNEL_PREFIX}${sessionId}`;

export const getVoiceLiveInspectorWindowName = (sessionId: string) =>
  `${VOICE_LIVE_INSPECTOR_WINDOW_PREFIX}${sessionId}`;
